/**
 * Every colour the console can set on a notice, in the order the server's
 * `NoticeAppearanceColorsIn` declares them. Keys outside this list are ignored,
 * so a newer console never breaks an older SDK.
 */
export const NOTICE_COLOR_KEYS = [
  "background",
  "surface",
  "heading",
  "text",
  "muted_text",
  "link",
  "border",
  "overlay",
  "accept_all_bg",
  "accept_all_text",
  "accept_bg",
  "accept_text",
  "decline_bg",
  "decline_text",
  "decline_border",
  "language_bg",
  "language_text",
  "language_selected_bg",
  "language_selected_text",
  "toggle_on",
  "toggle_off",
  "toggle_knob",
  "tts_highlight_bg",
  "tts_highlight_text",
] as const;

export const NOTICE_SELECTION_CONTROLS = ["checkbox", "radio", "dropdown"] as const;

/** How checkbox rows are drawn: native checkboxes, or switches. */
export const NOTICE_CONTROL_STYLES = ["checkbox", "switch"] as const;

/** Mirrors the server's clamp, so a hand-edited value still draws sensibly. */
export const NOTICE_RADIUS_MIN = 0;
export const NOTICE_RADIUS_MAX = 28;

/** Phones: a centred modal, or a sheet docked to the bottom edge. */
export const NOTICE_MOBILE_LAYOUTS = ["modal", "sheet"] as const;

/** Where the notice sits on wider screens. */
export const NOTICE_DESKTOP_POSITIONS = [
  "center",
  "bottom-right",
  "bottom-left",
  "bottom-bar",
] as const;

/** The page behind the notice: dimmed with `colors.overlay`, or left as is. */
export const NOTICE_BACKDROPS = ["dim", "none"] as const;

/** Phone footer: three stacked buttons, or Accept All over the other two. */
export const NOTICE_PHONE_FOOTERS = ["stacked", "paired"] as const;

export const NOTICE_TEXT_SCALES = ["sm", "md", "lg"] as const;

export const DEFAULT_TEXT_SCALE = "md";

export const DEFAULT_DESKTOP_POSITION = "center";

/** Multiplies every text size the notice draws. */
export const NOTICE_TEXT_SCALE_FACTORS = {
  sm: 0.92,
  md: 1,
  lg: 1.1,
} as const;

/** Desktop card width, in px; mirrors the server's clamp. */
export const NOTICE_WIDTH_MIN = 320;
export const NOTICE_WIDTH_MAX = 960;

/** Logo height, in px; mirrors the server's clamp. */
export const NOTICE_LOGO_SIZE_MIN = 16;
export const NOTICE_LOGO_SIZE_MAX = 96;

/**
 * The admin's custom CSS is refused whole when it is longer than this or
 * carries one of these tokens, the same guard the server and the cookie banner
 * apply. Checked again here in case something other than the console wrote it.
 */
export const NOTICE_CUSTOM_CSS_MAX_LENGTH = 20000;
export const NOTICE_DISALLOWED_CSS_TOKENS = [
  "</style",
  "@import",
  "javascript:",
  "expression(",
  "behavior:",
] as const;

/** The at-rule that keeps an admin's custom CSS inside the element it styles. */
export const CSS_SCOPE_AT_RULE = "@scope";

/** Characters escaped inside a quoted CSS attribute value. */
export const CSS_STRING_SPECIALS_RE = /["\\\n]/g;

/**
 * A CSS escape: `\` and up to six hex digits (one optional trailing
 * whitespace, where `\r\n` counts as one), or `\` and any literal character.
 * Decoded before the token scan, since a tokenizer reads `@im\70 ort` as
 * `@import`.
 */
export const CSS_ESCAPE_RE =
  /\\([0-9a-f]{1,6})(?:\r\n|[ \t\r\n\f])?|\\([\s\S])/gi;

/** Each CSS block opener and the closer that must end it. */
export const CSS_BRACKET_PAIRS: Readonly<Record<string, string>> = {
  "{": "}",
  "(": ")",
  "[": "]",
};
export const CSS_CLOSING_BRACKETS: ReadonlyArray<string> =
  Object.values(CSS_BRACKET_PAIRS);

/** The characters that open a CSS string, and the newlines that end one early. */
export const CSS_QUOTES: ReadonlyArray<string> = ['"', "'"];
export const CSS_NEWLINES: ReadonlyArray<string> = ["\n", "\r", "\f"];

/** What a CSS escape of NULL, a surrogate or an out-of-range code decodes to. */
export const CSS_REPLACEMENT_CHARACTER = "\uFFFD";
export const MAX_CODE_POINT = 0x10ffff;
export const HEX_RADIX = 16;
export const SURROGATE_MIN = 0xd800;
export const SURROGATE_MAX = 0xdfff;

/** Served colours are hex only: `#RGB`, `#RRGGBB` or `#RRGGBBAA`. */
export const NOTICE_HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** The talkback highlight every platform draws when the admin sets none. */
export const DEFAULT_TTS_HIGHLIGHT_BG = "#FFF9C4";

/**
 * Custom properties the notice root sets from the console palette. The injected
 * stylesheet reads each with its classic value as the fallback, so a notice
 * whose admin set no colour renders exactly as before.
 */
export const NOTICE_THEME_VARS = {
  accent: "--redacto-accent",
  controlOff: "--redacto-control-off",
  knob: "--redacto-toggle-knob",
  ttsBackground: "--redacto-tts-bg",
  ttsText: "--redacto-tts-text",
} as const;

// Each palette colour as the injected stylesheet reads it, falling back to the
// value classic always drew with.
export const THEMED_ACCENT = `var(${NOTICE_THEME_VARS.accent}, #4f87ff)`;
export const THEMED_CONTROL_OFF = `var(${NOTICE_THEME_VARS.controlOff}, #d0d5dd)`;
export const THEMED_TTS_BACKGROUND = `var(${NOTICE_THEME_VARS.ttsBackground}, ${DEFAULT_TTS_HIGHLIGHT_BG})`;

/** Present on the notice root only when the admin set a talkback text colour. */
export const TTS_TEXT_ATTRIBUTE = "data-redacto-tts-text";
